import React, { memo, useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import ExpandedIcon from '@/pages/datalab/components/ChartCardIconButtons/ExpandedIcon';
import { useCollapse } from './hooks/useCollapse';
import type { ICollapseCardProps } from './types';
import './index.scss';

/**
 * CollapseCard：带可折叠内容区域的通用卡片容器。
 *
 * 职责：
 * - 提供统一的卡片布局（标题区 + 右侧操作区 + 可折叠内容区）
 * - 支持受控/非受控两种折叠模式，并对折叠状态变更进行通知
 *
 * 约束：
 * - 展开/收起状态可以受控，也可以内部自管理（defaultCollapsed）
 * - 折叠/展开仅通过图标或显式回调触发，避免点击标题误触
 */
const CollapseCard: React.FC<ICollapseCardProps> = (props) => {
  const {
    title,
    extra,
    children,
    collapsed: controlledCollapsed,
    defaultCollapsed = false,
    onCollapseChange,
    className,
    style,
  } = props;

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { collapsed, toggleCollapsed, maxHeight } = useCollapse({
    collapsed: controlledCollapsed,
    defaultCollapsed,
    onCollapseChange,
    contentRef,
    animationDuration: 300,
    enableAnimation: true,
  });

  const handleToggle = useCallback(() => {
    toggleCollapsed();
  }, [toggleCollapsed]);

  const contentStyle = useMemo<React.CSSProperties>(
    () => ({
      maxHeight,
    }),
    [maxHeight]
  );

  return (
    <div className={classNames('dbl-collapse-card', className)} style={style}>
      <div className="dbl-collapse-card__header">
        <div className="dbl-collapse-card__title">{title}</div>

        {!!extra && <div className="dbl-collapse-card__extra">{extra}</div>}

        <div className="dbl-collapse-card__toggle">
          <ExpandedIcon expanded={!collapsed} onChange={handleToggle} />
        </div>
      </div>

      <div
        ref={contentRef}
        className={classNames('dbl-collapse-card__content', {
          'dbl-collapse-card__content--collapsed': collapsed,
        })}
        style={contentStyle}
      >
        {/* TODO-[优化]: 条件渲染 `{!collapsed ? children : null}` 会在折叠时卸载内容，
            导致无法计算展开前的自然高度，从而破坏 max-height 过渡动画。
            后续应改为始终渲染 children，依靠外层容器的 max-height + overflow:hidden
            裁切内容，并配合 CSS 的 visibility/pointer-events 在折叠态下禁用内部交互。 */}
        {!collapsed ? children : null}
      </div>
    </div>
  );
};

export default memo(CollapseCard);

